export interface ObjectChange<T = unknown> {
  id: number;
  oldObject: T;
  newObject: T;
}

/**
 * Generates an array of object changes by comparing consecutive objects in a sorted array.
 *
 * Takes an array of objects and sorts them by a specified date field from newest to oldest,
 * then creates pairs of consecutive objects to represent transitions/changes between states.
 *
 * @template T - The type of objects in the array, must extend Record<string, any>
 * @param objects - Array of objects to compare
 * @param compareFieldName - Name of the date field to sort by (should contain date strings)
 * @returns Array of ObjectChange objects, each containing id (historical step number, oldest = 1), oldObject (previous state) and newObject (current state)
 *
 * @example
 * ```typescript
 * const prescriptions = [
 *   { id: 1, name: 'Medicine A', lastUpdateDate: '2023-01-01' },
 *   { id: 2, name: 'Medicine B', lastUpdateDate: '2023-01-03' },
 *   { id: 3, name: 'Medicine C', lastUpdateDate: '2023-01-02' }
 * ];
 *
 * const changes = generateObjectChanges(prescriptions, 'lastUpdateDate');
 * // Returns: [
 * //   { id: 2, oldObject: { id: 3, name: 'Medicine C', ... }, newObject: { id: 2, name: 'Medicine B', ... } },
 * //   { id: 1, oldObject: { id: 1, name: 'Medicine A', ... }, newObject: { id: 3, name: 'Medicine C', ... } }
 * // ]
 * ```
 */
export function generateObjectChanges<T extends Record<string, any>>(objects: T[], compareFieldName: keyof T): ObjectChange<T>[] {
  if (objects.length < 2) {
    return [];
  }

  // Sort objects by the compare field (newest to oldest)
  const sortedObjects: T[] = [...objects].sort((a: T, b: T): number => {
    const dateA = new Date(<string>a[compareFieldName]);
    const dateB = new Date(<string>b[compareFieldName]);
    return dateB.getTime() - dateA.getTime();
  });

  const changes: ObjectChange<T>[] = [];

  sortedObjects.forEach((currentObject: T, i: number): void => {
    if (i < sortedObjects.length - 1) {
      const previousObject: T = sortedObjects[i + 1];

      changes.push({
        id: sortedObjects.length - (i + 1),
        oldObject: previousObject,
        newObject: currentObject,
      });
    }
  });

  return changes;
}
